import { inject, Injectable } from '@angular/core';
import { AbstractControl, FormGroup } from '@angular/forms';
import { ConditionModel, GroupItemModel, isGroupItem, isInputItem, ItemBaseModel, ItemStateType } from '@libs/form/models';
import { Subscription } from 'rxjs';
import { ConditionEvaluatorFactory } from './condition-evaluator.factory';
import { ConditionEvaluator } from './evaluator';
import { StateHandler } from './handler';
import { StateHandlerFactory } from './state-handler.factory';

@Injectable()
export class ConditionalFieldsService {
  private readonly conditionEvaluatorFactory: ConditionEvaluatorFactory = inject(ConditionEvaluatorFactory);
  private readonly stateHandlerFactory: StateHandlerFactory = inject(StateHandlerFactory);

  public initialize(data: GroupItemModel, form: FormGroup): Subscription {
    const controls: Record<string, AbstractControl> = {};

    this.mapControlNames(form, data.name, controls);

    const subscription: Subscription = new Subscription();

    this.processItem(data, controls, subscription);

    return subscription;
  }

  private processItem(item: ItemBaseModel, controls: Record<string, AbstractControl>, subscription: Subscription): void {
    this.createConditions(item, controls, subscription);

    if (isGroupItem(item)) {
      item.items.forEach(
        child => void this.processItem(child, controls, subscription),
      );
    }
  }

  private createConditions(item: ItemBaseModel, controls: Record<string, AbstractControl>, subscription: Subscription): void {
    if (item.conditions === undefined) {
      return;
    }

    let control: AbstractControl | undefined;

    if (isInputItem(item) || isGroupItem(item)) {
      control = controls[item.name];
    }

    for (const state in item.conditions) {
      const condition: ConditionModel = item.conditions[state as ItemStateType] as ConditionModel;

      const dependency: AbstractControl | undefined = controls[condition.dependency];

      if (dependency === undefined) {
        console.error(`Dependency '${condition.dependency}' wasn't found`);

        continue;
      }

      const evaluator: ConditionEvaluator | undefined = this.conditionEvaluatorFactory.create(dependency, condition);

      if (evaluator === undefined) {
        console.error(`Could not create evaluator for condition operator '${condition.operator}'`);

        continue;
      }

      const handler: StateHandler | undefined = this.stateHandlerFactory.create(state as ItemStateType, evaluator, item, control);

      if (handler === undefined) {
        console.error(`Could not create handler for condition state '${state}'`);

        continue;
      }

      const valueSubscription: Subscription = dependency.valueChanges.subscribe(handler.handle.bind(handler));
      const statusSubscription: Subscription = dependency.statusChanges.subscribe(handler.handle.bind(handler));

      dependency.updateValueAndValidity();

      subscription.add(valueSubscription);
      subscription.add(statusSubscription);
    }
  }

  private mapControlNames(control: AbstractControl, name: string, result: Record<string, AbstractControl>): void {
    result[name] = control;

    if (control instanceof FormGroup) {
      Object.keys(control.controls).forEach(childName => {
        this.mapControlNames(control.controls[childName], childName, result);
      });
    }
  }
}
